/* eslint-disable no-param-reassign */
import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { dbdsApi } from 'services/apiDigitalTicket/dbds';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// File Saver

// Helpers
import { getDbdPlace } from 'helpers/getDbdPlace';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';
import CONST_DBD from 'constants/dbd';

import * as DbdsActions from './actions';
import { DbdInterface, DbdsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof DbdsActions.listDbdsRequest>): any {
  try {
    const { isStatushistory } = payload;

    if (!isStatushistory) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando DBDs' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DbdsActions.listDbdsSuccess({
          dbdsList: Array.isArray(response.data)
            ? response.data?.map((dbd: DbdInterface) => ({
                ...dbd,
                version: dbd?.statusData?.applicationVersion ?? '',
                dbdPlace: getDbdPlace(dbd),
                type:
                  CONST_DBD.TYPE.find(({ KEY }) => KEY === dbd.type)?.VALUE ??
                  '',
                spin:
                  CONST_DBD.WAY.find(({ KEY }) => KEY === dbd.way)?.VALUE ?? '',
                conectivityStatus:
                  CONST_DBD.CONECTIVITY_STATUSES.find(
                    ({ KEY }) => KEY === dbd?.statusData?.internetStatus,
                  )?.VALUE ?? '',
              }))
            : [],
        }),
      );
      if (!isStatushistory) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - listDbds - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.listDbdsFailure());
    if (!payload?.isStatushistory) {
      yield put(DialogsActions.setClose());
    }
  }
}

function* monitoring({
  payload,
}: ReturnType<typeof DbdsActions.getDbdMonitoringRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    if (!transportOperator?.id) {
      yield put(DbdsActions.getDbdMonitoringFailure());
      return;
    }

    const response = yield call(dbdsApi.monitoring, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DbdsActions.getDbdMonitoringSuccess({
          monitoringData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - monitoring - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.getDbdMonitoringFailure());
    yield put(DialogsActions.setClose());
  }
}

function* telemetry({
  payload,
}: ReturnType<typeof DbdsActions.getDbdTelemetryRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando telemetria do DBD' }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    if (!transportOperator?.id) {
      yield put(DbdsActions.getDbdTelemetryFailure());
      yield put(DialogsActions.setClose());
      return;
    }

    const response = yield call(dbdsApi.dbdTelemetry, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DbdsActions.getDbdTelemetrySuccess({
          telemetryData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - telemetry - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.getDbdTelemetryFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({ payload }: ReturnType<typeof DbdsActions.getDbdRequest>): any {
  const { hasLoadingSkaleton } = payload;
  try {
    if (!hasLoadingSkaleton) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DbdsActions.getDbdSuccess({ dbdData: response.data }));
      if (!hasLoadingSkaleton) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - getDbd - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.getDbdFailure());
    yield put(DialogsActions.setClose());
  }
}

function* dbdVersionRequestList({
  payload,
}: ReturnType<typeof DbdsActions.dbdVersionRequestListRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.dbdVersionRequest, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DbdsActions.dbdVersionRequestListSuccess({
          dbdVersionRequestList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - dbdVersionRequestList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.dbdVersionRequestListFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof DbdsActions.createDbdRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando DBD' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DbdsActions.createDbdSuccess({ dbdData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'DBD criado com sucesso',
        }),
      );
      yield put(push(PATH.DBDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - createDbd - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.createDbdFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof DbdsActions.updateDbdRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando DBD' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DbdsActions.updateDbdSuccess({ dbdData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'DBD alterado com sucesso',
        }),
      );
      yield put(
        DbdsActions.listDbdsRequest({ _includes: 'sections,car,employee' }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - updateDbd - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.updateDbdFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof DbdsActions.deleteDbdRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Deletando DBD' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DbdsActions.deleteDbdSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'DBD excluído com sucesso',
        }),
      );
      yield put(
        DbdsActions.listDbdsRequest({ _includes: 'sections,car,employee' }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.deleteDbdFailure());
    yield put(DialogsActions.setClose());
  }
}

function* requestStatus({
  payload,
}: ReturnType<typeof DbdsActions.requestStatusRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Atualizando status' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(dbdsApi.requestStatus, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DbdsActions.requestStatusSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Status do DBD atualizada com sucesso',
        }),
      );
      yield put(
        DbdsActions.listDbdsRequest({
          _includes: 'sections,car,block,gate,employee',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - requestStatus - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.requestStatusFailure());
    yield put(DialogsActions.setClose());
  }
}

function* dbdVersionlist({
  payload,
}: ReturnType<typeof DbdsActions.getDbdVersionListRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(dbdsApi.versionList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DbdsActions.getDbdVersionListSuccess({
          dbdVersionList: response.data,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - Version List - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DbdsActions.getDbdVersionListFailure());
  }
}

function* dbdVersionData({
  payload,
}: ReturnType<typeof DbdsActions.getDbdVersionDataRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(dbdsApi.versionList, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        DbdsActions.getDbdVersionDataSuccess({
          dbdVersionData: response.data,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbd - Version - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DbdsActions.getDbdVersionDataFailure());
  }
}

function* versionUpdate({
  payload,
}: ReturnType<typeof DbdsActions.versionUpdateRequest>): any {
  try {
    const { type, ...rest } = payload;

    yield put(
      DialogsActions.setOpenLoading({
        title: `${
          type === 'update' ? 'Atualizando' : 'Restaurando'
        } versão do(s) dbd(s)`,
      }),
    );

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(dbdsApi.versionUpdateRequest, {
      data: { transportOperatorId: transportOperator.id, ...rest },
    });

    if (response.status === 200) {
      yield put(DbdsActions.versionUpdateSuccess());
      yield put(DialogsActions.setClose());
      if (Array.isArray(response.data)) {
        const result = response.data.filter(
          (el: { dbdId: string; success: boolean }) => el?.success,
        );
        const erroResult = response.data.filter(
          (el: { dbdId: string; success: boolean }) => !el?.success,
        );
        if (result.length > 0) {
          yield put(
            SnackbarsActions.setOpen({
              open: true,
              type: 'success',
              message: `${result.length} ${
                result.length === 1 ? 'Dbd foi ' : 'Dbds foram '
              } ${type === 'update' ? 'atualizado' : 'restaurado'}`,
            }),
          );
        }
        if (erroResult.length > 0) {
          yield put(
            SnackbarsActions.setSecondOpen({
              open: true,
              type: 'error',
              message: `Ocorreu um error ao tentar ${
                type === 'update' ? 'atualizar' : 'restaurar'
              } ${result.length}  Dbd(s)`,
            }),
          );
        }
      }

      yield put(
        DbdsActions.listDbdsRequest({
          _includes: 'car,block,sections,gate,employee',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbd - Version Update Request - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(DbdsActions.versionUpdateFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createMany({
  payload,
}: ReturnType<typeof DbdsActions.createManyDbdRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando DBDs' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const data = payload.map((obj) => ({
      ...obj,
      transportOperatorId: transportOperator.id,
    }));

    const response = yield call(dbdsApi.createMany, {
      data,
    });

    if (response.status === 200) {
      yield put(DbdsActions.createManyDbdSuccess({ dbdsList: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'DBDs criados com sucesso',
        }),
      );
      yield put(push(PATH.DBDS.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('dbds - createManyDbd - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DbdsActions.createManyDbdFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const dbdsSagas = [
  takeLatest(DbdsTypes.LIST_DBDS_REQUEST, list),
  takeLatest(DbdsTypes.GET_DBD_MONITORING_REQUEST, monitoring),
  takeLatest(DbdsTypes.GET_DBD_TELEMETRY_REQUEST, telemetry),
  takeLatest(DbdsTypes.GET_DBD_REQUEST, get),
  takeLatest(DbdsTypes.CREATE_DBD_REQUEST, create),
  takeLatest(DbdsTypes.UPDATE_DBD_REQUEST, update),
  takeLatest(DbdsTypes.DELETE_DBD_REQUEST, remove),
  takeLatest(DbdsTypes.DBD_VERSION_REQUEST_LIST_REQUEST, dbdVersionRequestList),
  takeLatest(DbdsTypes.REQUEST_STATUS_REQUEST, requestStatus),
  takeLatest(DbdsTypes.GET_DBD_VERSION_LIST_REQUEST, dbdVersionlist),
  takeLatest(DbdsTypes.GET_DBD_VERSION_REQUEST, dbdVersionData),
  takeLatest(DbdsTypes.VERSION_UPDATE_REQUEST_REQUEST, versionUpdate),
  takeLatest(DbdsTypes.CREATE_MANY_DBD_REQUEST, createMany),
];
